import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast({ title: 'Success', description: 'Settings updated successfully' });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast({ title: 'Error', description: 'Failed to update settings', variant: 'destructive' });
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1 className="text-3xl font-light text-neutral-900 mb-8">Settings</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-light">Store Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Store Name</Label>
              <Input
                value={settings?.store_name || ''}
                onChange={(e) => setSettings({...settings, store_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={settings?.tagline || ''}
                onChange={(e) => setSettings({...settings, tagline: e.target.value})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-light">Shipping Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Free Shipping Threshold (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings?.free_shipping_threshold || ''}
                  onChange={(e) => setSettings({...settings, free_shipping_threshold: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Standard Shipping Cost (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings?.standard_shipping_cost || ''}
                  onChange={(e) => setSettings({...settings, standard_shipping_cost: parseFloat(e.target.value)})}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-light">Stripe Integration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Stripe Publishable Key</Label>
              <Input
                type="password"
                placeholder="pk_test_..."
                value={settings?.stripe_publishable_key || ''}
                onChange={(e) => setSettings({...settings, stripe_publishable_key: e.target.value})}
              />
              <p className="text-xs text-neutral-500 mt-1">Your Stripe publishable key (starts with pk_test_)</p>
            </div>
            <div>
              <Label>Stripe Secret Key</Label>
              <Input
                type="password"
                placeholder="sk_test_..."
                value={settings?.stripe_secret_key || ''}
                onChange={(e) => setSettings({...settings, stripe_secret_key: e.target.value})}
              />
              <p className="text-xs text-neutral-500 mt-1">Your Stripe secret key (starts with sk_test_)</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <p className="font-semibold mb-2">How to get Stripe keys:</p>
              <ol className="list-decimal list-inside space-y-1 text-neutral-700">
                <li>Go to <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">stripe.com</a> and sign up</li>
                <li>Navigate to Developers → API Keys</li>
                <li>Copy both Test keys and paste them here</li>
                <li>Save settings to activate payments</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" size="lg" className="bg-neutral-900 hover:bg-neutral-800">
          Save Settings
        </Button>
      </form>
    </div>
  );
};

export default Settings;